import { faUtensils } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";
import useSWR from "swr";
import useApi from "../../../../../../../utils/api";
import { useAccess } from "../../../../../../../utils/hooks/access";
import { useVisible } from "../../../../../../../utils/hooks/useVisible";
import WaiveModal from "./WaiveModal";

const WaiveTooltip = ({ data, waive, cancelWaive, isMyTimesheet, timesheets }) => {
    const { t } = useTranslation();
    const { visible: tooltipVisible, toggle: toggleTooltip } = useVisible();
    const id = `meal-waive-${data.user.id}-row-${data.id}`;
    const {
        visible: confirmationModalVisible,
        open: openConfirmationModal,
        close: closeConfirmationModal,
    } = useVisible();

    const { authGet } = useApi();
    const {
        data: mealPremiumRule,
        isLoading: isLoadingMealPremiumRule,
    } = useSWR(
        () => {
            if (!data?.calculationGroup?.mealPremium?.id) {
                return null;
            }
            return `/calc-rules/meal-premium/${data.calculationGroup.mealPremium.id}`;
        },
        (url) => {
            return authGet(url);
        },
    );

    const mealPremiums = timesheets.filter((item) => {
        return item.appliedRules?.includes("mealPremium");
    });
    const waivableViolation = mealPremiums.find((item) => {
        return item.appliedRules?.includes("mealPremium--waivable");
    });
    const waivedPremium = mealPremiums.find((item) => {
        return !!item.mealWaiveStatus;
    });
    const isWaivedByManager = !!waivedPremium?.appliedRules?.includes("mealPremium--managerWaive");

    const { hasAccess: canCreateMealWaiveRequest } = useAccess("requests.timesheet.mealPremiumWaive.canCreate");
    const { hasAccess: canApproveMealWaiveRequest } = useAccess("requests.timesheet.mealPremiumWaive.team.canApprove");
    const canWaive = (
        (
            isMyTimesheet
            && mealPremiumRule?.canWaive
            && !waivedPremium
            && canCreateMealWaiveRequest
            && waivableViolation
        )
        || (
            !isMyTimesheet
            && mealPremiumRule?.canManagerWaive
            && (isWaivedByManager || !waivedPremium)
            && canApproveMealWaiveRequest
            && waivableViolation
        )
    );

    if (!data.mealWaiveStatus && !canWaive) {
        return null;
    }

    // TODO: depending on the user who waived the premium, the approved tooltip should show either that the request was approved or that the manager waived the premium itself

    return (
        <span id={id}>
            <FontAwesomeIcon
                className={classNames(
                    (!waivedPremium && isMyTimesheet) && "text-primary",
                    waivedPremium?.mealWaiveStatus === "approved" && "text-info",
                    "cursor-pointer"
                )}
                icon={faUtensils}
                size="lg"
                onClick={
                    canWaive
                        ? () => openConfirmationModal()
                        : undefined
                }
            />

            <Tooltip target={id} isOpen={tooltipVisible} toggle={toggleTooltip}>
                <span className="text-sm font-weight-bolder px-2 m-2">
                    {!waivedPremium?.mealWaiveStatus && canWaive && t("click-to-waive-meal-premium")}
                    {!waivedPremium?.mealWaiveStatus && !canWaive && t("meal-premium-not-available-for-waive")}
                    {waivedPremium?.mealWaiveStatus === "pending" && t("meal-waive-request-info")}
                    {waivedPremium?.mealWaiveStatus === "approved" && t("meal-waive-request-approved")}
                </span>
            </Tooltip>

            <WaiveModal
                isOpen={confirmationModalVisible}
                onClose={closeConfirmationModal}
                onConfirm={(values) => isWaivedByManager
                    ? cancelWaive(data.date, values)
                    : waive(data.date, values)
                }
                canWaive={canWaive}
                mealPremiumRule={mealPremiumRule}
                isLoadingMealPremiumRule={isLoadingMealPremiumRule}
                isMyTimesheet={isMyTimesheet}
                mealPremiums={mealPremiums}
                isWaivedByManager={isWaivedByManager}
            />
        </span>
    )
}

export default WaiveTooltip;
